import { rem, Styles } from '@mantine/core';
import { BaseSelectStylesNames } from '@mantine/core/lib/Select/types';
import { GridApi } from 'ag-grid-community';
import { DeviceSelect } from 'components/selects/DeviceSelect';
import useFilterValue, { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { useMemo } from 'react';
import { P4Medium } from 'theme/typography';

/**
 * The key of the filter. Must be equal to the column field.
 */
export const FILTER_KEY = 'parentDevice.deviceId' as const;

/**
 * Parameters of the ParentDeviceSelectFilter component.
 */
export interface ParentDeviceSelectFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
}

interface ConfigureParentDeviceSelectFilterProps {
  placeholder?: string;
}

type FullParentDeviceSelectFilterProps = ParentDeviceSelectFilterProps & ConfigureParentDeviceSelectFilterProps;

/**
 * Filter component for the deviceId column.
 *
 * This filter assumes:
 *  - column field is `deviceId`
 */
export default function ParentDeviceSelectFilter({
  api,
  onChange,
  placeholder = 'Všetky zariadenia',
}: FullParentDeviceSelectFilterProps) {
  const [value, setValue] = useFilterValue({ api, filterKey: FILTER_KEY, onChange });

  const styles: Styles<BaseSelectStylesNames, Record<string, any>> = useMemo(
    () => ({
      wrapper: {
        '&::before': {
          content: '"Nadradené: "',
          display: 'flex',
          alignItems: 'center',
          position: 'absolute',
          left: rem(8),
          height: '100%',
          color: 'var(--mantine-color-gray-4)',
          pointerEvents: 'none',
          ...P4Medium,
        },
      },
      input: {
        paddingLeft: rem(76),
      },
    }),
    []
  );

  return (
    <DeviceSelect
      lazyLoad
      clearable
      w="100%"
      size="sm"
      placeholder={placeholder}
      showDiscarded={false}
      value={String(value)}
      onChange={setValue}
      onlyParents
      styles={styles}
    />
  );
}

ParentDeviceSelectFilter.configure =
  (config: ConfigureParentDeviceSelectFilterProps) => (props: ParentDeviceSelectFilterProps) =>
    <ParentDeviceSelectFilter {...config} {...props} />;
